import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { 
  GraduationCap, 
  ArrowRight,
  Users,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  FileText,
  Loader2,
  Save,
  MessageSquare,
  ChevronLeft,
  ChevronRight,
  PenTool
} from 'lucide-react';
import { toast } from 'sonner';

const EssayGrading = () => {
  const { examId } = useParams();
  const navigate = useNavigate();
  const { token, isTeacher } = useAuth();
  
  const [exam, setExam] = useState(null);
  const [essayAnswers, setEssayAnswers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [gradeInput, setGradeInput] = useState('');
  const [feedbackInput, setFeedbackInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (!isTeacher) {
      navigate('/teacher/login');
      return;
    }
    fetchEssayAnswers();
  }, [isTeacher, examId]);

  const fetchEssayAnswers = async () => {
    setLoading(true);
    try {
      // Fetch exam details
      const examResponse = await fetch(`${API_URL}/api/exams/${examId}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (examResponse.ok) {
        const examData = await examResponse.json();
        setExam(examData);
      }

      // Fetch essay answers
      const response = await fetch(`${API_URL}/api/teacher/essay-answers/${examId}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setEssayAnswers(data);
        if (data.length > 0) {
          selectAnswer(data[0], 0);
        }
      } else {
        toast.error('فشل تحميل الإجابات');
      }
    } catch (error) {
      console.error('Error fetching essay answers:', error);
      toast.error('حدث خطأ');
    }
    setLoading(false);
  };

  const selectAnswer = (answer, index) => {
    setSelectedAnswer(answer);
    setCurrentIndex(index);
    setGradeInput(answer.grade !== null ? String(answer.grade) : '');
    setFeedbackInput(answer.feedback || '');
  };

  const handleSaveGrade = async () => {
    if (!selectedAnswer) return;
    
    const score = parseInt(gradeInput);
    if (isNaN(score) || score < 0 || score > selectedAnswer.question_points) {
      toast.error(`الدرجة يجب أن تكون بين 0 و ${selectedAnswer.question_points}`);
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/teacher/essay-answers/grade`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          session_id: selectedAnswer.session_id,
          question_id: selectedAnswer.question_id,
          score: score,
          feedback: feedbackInput,
        }),
      });

      if (response.ok) {
        toast.success('تم حفظ التقييم بنجاح');
        
        // Update local state
        setEssayAnswers(prev => prev.map((ans, idx) => 
          idx === currentIndex 
            ? { ...ans, graded: true, grade: score, feedback: feedbackInput }
            : ans
        ));
        setSelectedAnswer(prev => ({ ...prev, graded: true, grade: score, feedback: feedbackInput }));
        
        // Move to next ungraded
        const nextUngraded = essayAnswers.findIndex((a, i) => i > currentIndex && !a.graded);
        if (nextUngraded !== -1) {
          selectAnswer(essayAnswers[nextUngraded], nextUngraded);
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'فشل حفظ التقييم');
      }
    } catch (error) {
      console.error('Error saving grade:', error);
      toast.error('حدث خطأ أثناء الحفظ');
    }
    setSaving(false);
  };

  const goToNext = () => {
    if (currentIndex < essayAnswers.length - 1) {
      selectAnswer(essayAnswers[currentIndex + 1], currentIndex + 1);
    }
  };

  const goToPrev = () => {
    if (currentIndex > 0) {
      selectAnswer(essayAnswers[currentIndex - 1], currentIndex - 1);
    }
  };

  const gradedCount = essayAnswers.filter(a => a.graded).length;
  const pendingCount = essayAnswers.length - gradedCount;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFFFFF] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-[#d4a853] mx-auto mb-4" />
          <p className="text-[#6B4C2A]">جاري تحميل الإجابات...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFFFFF]">
      {/* Header */}
      <header className="bg-[#FFFFFF]/80 border-b border-[#E8DFCF] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/teacher/dashboard" className="flex items-center gap-3">
              <img src="/logo.png" alt="Logo" className="w-10 h-10 rounded-lg object-cover border border-[#d4a853]/30" />
              <span className="text-xl font-bold text-[#d4a853]">المعتمد في اللغة العربية</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/teacher/dashboard')}
            className="text-[#6B4C2A] hover:text-[#412402] mb-4"
          >
            <ArrowRight className="w-4 h-4 ml-2" />
            العودة للوحة التحكم
          </Button>
          <h1 className="text-2xl font-bold text-[#412402]">تصحيح الأسئلة المقالية</h1>
          <p className="text-[#6B4C2A] mt-1">{exam?.title}</p>
        </div>

        {/* Progress Stats */}
        <div className="grid sm:grid-cols-3 gap-4 mb-6">
          <Card className="border-[#E8DFCF]">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <FileText className="w-6 h-6 text-blue-700" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#412402]">{essayAnswers.length}</p>
                <p className="text-sm text-[#6B4C2A]">إجمالي الإجابات</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-[#E8DFCF]">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-[#d4a853]/15 rounded-xl flex items-center justify-center">
                <Clock className="w-6 h-6 text-[#d4a853]" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#d4a853]">{pendingCount}</p>
                <p className="text-sm text-[#6B4C2A]">بانتظار التصحيح</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-[#E8DFCF]">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-700" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-600">{gradedCount}</p>
                <p className="text-sm text-[#6B4C2A]">تم تصحيحها</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Progress Bar */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-[#6B4C2A]">نسبة التقدم</span>
            <span className="text-sm font-medium text-[#412402]">
              {essayAnswers.length > 0 ? Math.round((gradedCount / essayAnswers.length) * 100) : 0}%
            </span>
          </div>
          <Progress 
            value={essayAnswers.length > 0 ? (gradedCount / essayAnswers.length) * 100 : 0} 
            className="h-2"
          />
        </div>

        {essayAnswers.length === 0 ? (
          <Card className="border-[#E8DFCF]">
            <CardContent className="py-12 text-center">
              <PenTool className="w-16 h-16 text-[#E8DFCF] mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-[#412402] mb-2">لا توجد إجابات مقالية</h3>
              <p className="text-[#8B7355]">لم يقم أي طالب بالإجابة على الأسئلة المقالية بعد</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Answers List */}
            <div className="lg:col-span-1">
              <Card className="border-[#E8DFCF]">
                <CardHeader className="border-b border-[#E8DFCF]">
                  <CardTitle className="text-base">قائمة الإجابات</CardTitle>
                </CardHeader>
                <CardContent className="p-0 max-h-[600px] overflow-y-auto">
                  {essayAnswers.map((answer, index) => (
                    <div
                      key={`${answer.session_id}-${answer.question_id}`}
                      onClick={() => selectAnswer(answer, index)}
                      className={`p-4 border-b border-[#E8DFCF] cursor-pointer transition-colors ${
                        currentIndex === index 
                          ? 'bg-[#d4a853]/10 border-r-4 border-r-emerald-600' 
                          : 'hover:bg-[#FFFFFF]'
                      }`}
                      data-testid={`essay-answer-${index}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="font-medium text-[#412402] text-sm">{answer.student_name}</p>
                          <p className="text-xs text-[#8B7355] mt-1 line-clamp-1">
                            {answer.question_text}
                          </p>
                        </div>
                        {answer.graded ? (
                          <Badge className="bg-green-100 text-green-800 text-xs">
                            {answer.grade}/{answer.question_points}
                          </Badge>
                        ) : (
                          <Badge variant="secondary" className="text-xs">جديد</Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>

            {/* Grading Panel */}
            <div className="lg:col-span-2">
              {selectedAnswer && (
                <Card className="border-[#E8DFCF]">
                  <CardHeader className="border-b border-[#E8DFCF]">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-lg">{selectedAnswer.student_name}</CardTitle>
                        <CardDescription>
                          الإجابة {currentIndex + 1} من {essayAnswers.length}
                        </CardDescription>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={goToPrev}
                          disabled={currentIndex === 0}
                        >
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={goToNext}
                          disabled={currentIndex === essayAnswers.length - 1}
                        >
                          <ChevronLeft className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="p-6 space-y-6">
                    {/* Question */}
                    <div>
                      <Label className="text-[#412402] mb-2 block">السؤال</Label>
                      <div className="bg-[#FFFFFF] p-4 rounded-lg border border-[#E8DFCF]">
                        <p className="text-[#412402]">{selectedAnswer.question_text}</p>
                        <p className="text-sm text-[#8B7355] mt-2">
                          الدرجة القصوى: {selectedAnswer.question_points}
                        </p>
                      </div>
                    </div>

                    {/* Student Answer */}
                    <div>
                      <Label className="text-[#412402] mb-2 block">إجابة الطالب</Label>
                      <div className="bg-[#FFFFFF]/80 p-4 rounded-lg border-2 border-[#E8DFCF] min-h-[150px]">
                        <p className="text-[#412402] whitespace-pre-wrap">
                          {selectedAnswer.answer || '(لم يتم الإجابة)'}
                        </p>
                      </div>
                    </div>

                    {/* Grading Input */}
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="grade" className="text-[#412402] mb-2 block">
                          الدرجة (من {selectedAnswer.question_points})
                        </Label>
                        <Input
                          id="grade"
                          type="number"
                          min="0"
                          max={selectedAnswer.question_points}
                          value={gradeInput}
                          onChange={(e) => setGradeInput(e.target.value)}
                          className="text-lg font-semibold"
                          data-testid="grade-input"
                        />
                      </div>
                      <div className="flex items-end">
                        <div className="flex gap-2 w-full">
                          <Button
                            variant="outline"
                            className="flex-1"
                            onClick={() => setGradeInput('0')}
                          >
                            0
                          </Button>
                          <Button
                            variant="outline"
                            className="flex-1"
                            onClick={() => setGradeInput(String(Math.floor(selectedAnswer.question_points / 2)))}
                          >
                            نصف
                          </Button>
                          <Button
                            variant="outline"
                            className="flex-1"
                            onClick={() => setGradeInput(String(selectedAnswer.question_points))}
                          >
                            كامل
                          </Button>
                        </div>
                      </div>
                    </div>

                    {/* Feedback */}
                    <div>
                      <Label htmlFor="feedback" className="text-[#412402] mb-2 block">
                        ملاحظات للطالب (اختياري)
                      </Label>
                      <Textarea
                        id="feedback"
                        placeholder="ادخل ملاحظاتك هنا..."
                        value={feedbackInput}
                        onChange={(e) => setFeedbackInput(e.target.value)}
                        rows={3}
                        data-testid="feedback-input"
                      />
                    </div>

                    {/* Save Button */}
                    <Button
                      onClick={handleSaveGrade}
                      disabled={saving || !gradeInput}
                      className="w-full bg-gradient-to-r from-[#d4a853] to-[#8b5a2b] hover:from-[#f5d789] hover:to-[#d4a853]"
                      data-testid="save-grade-btn"
                    >
                      {saving ? (
                        <>
                          <Loader2 className="w-4 h-4 ml-2 animate-spin" />
                          جاري الحفظ...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4 ml-2" />
                          حفظ التقييم
                        </>
                      )}
                    </Button>

                    {selectedAnswer.graded && (
                      <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                        <p className="text-green-800 text-sm flex items-center gap-2">
                          <CheckCircle className="w-4 h-4" />
                          تم تقييم هذه الإجابة
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default EssayGrading;
